import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { isObject } from "lodash";

const fetchDoctypeShort = (doctype, fields) => {
    const params = {
        fields: fields ? JSON.stringify(fields) : [["name"]],
        limit_page_length: 0,
    };
    return axios.get(`resource/` + doctype, { params });
};

export const useGetDocTypeListShort = (doctype, fields) => {
    return useQuery(["GetDocTypeListShort", doctype], async () => {
        try {
            const response = await fetchDoctypeShort(doctype, fields);
            return response.data.data;
        } catch (e) {
            console.error(`Error while fetching GetSuppliers: ${e}`);
            throw e;
        }
    });
};

const fetchDocTypes = (docType, filters, fields, orderBy) => {
    let parsedFields = fields ? JSON.stringify(fields) : "[]";
    const params = {
        // doctype: docType,
        filters: JSON.stringify(filters),
        fields: parsedFields,
        limit_start: 0,
        limit_page_length: 150,
    };
    if (orderBy) {
        params.order_by = orderBy;
    }
    return axios.get(`resource/${docType}`, { params });
};

export const useDocTypesData = (
    docType,
    filters = null,
    fields = [],
    onSuccess,
    onError
) => {
    return useQuery(
        [docType],
        async () => {
            const { data } = await fetchDocTypes(docType, filters, fields);
            return data.data;
        },
        {
            onSuccess,
            onError,
            enabled: true,
        }
    );
};

const updateDoctype = async (doctype, name, formData) => {
    try {
        const response = await axios.put(
            `resource/${doctype}/${name}`,
            formData
        );
        return response;
    } catch (error) {
        console.error(`Error while updating ${doctype} ( ${name} ) : ${error}`);
    }
};

export const useUpdateDoctypeData = () => {
    return useMutation(async ({ doctype, name, formData }) => {
        const data = await updateDoctype(doctype, name, formData);
        return data;
    });
};

const createDoctype = async (doctype, formData) => {
    try {
        const response = await axios.post(`resource/${doctype}`, formData);
        return response.data;
    } catch (error) {
        console.error(`Error while creating customer: ${error}`);
        return [];
    }
};

export const useCreateDoctypeData = () => {
    return useMutation(async ({ doctype, formData }) => {
        const data = await createDoctype(doctype, formData);
        return data;
    });
};

const getDocType = (docType, docname, filters, fields) => {
    const params = {
        doctype: docType,
        name: docname,
    };
    return axios.get(`method/frappe.desk.form.load.getdoc`, { params });
};

export const useDocTypeFetchDataConditional = (
    docType,
    docname,
    filters = null,
    fields = [],
    onSuccess,
    onError
) => {
    return useQuery(
        [docType, docname],
        async () => {
            const { data } = await getDocType(
                docType,
                docname,
                filters,
                fields
            );
            return data;
        },
        {
            onSuccess,
            onError,
            enabled: !!docname,
        }
    );
};

// Create a new doctype...
const createDoc = (doctype, value) => {
    return axios.post(`resource/${doctype}`, { ...value });
};

export const useCreateDoctype = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ doctype, value }) => {
            const data = await createDoc(doctype, value);
            return data;
        },
        {
            onSuccess: (data) => {
                queryClient.refetchQueries(["getItems"]);
            },
        }
    );
};

// Get incoming rate for an item
const getIncomingRate = (args) => {
    return axios.post(`method/erpnext.stock.utils.get_incoming_rate`, { args });
};

export const useGetIncomingRate = () => {
    return useMutation(
        async (args) => {
            const { data } = await getIncomingRate(args);
            return data.message;
        }
    );
};

const fetchList = (doctype, filters, fields, sortBy, parent) => {
    const params = {
        fields: JSON.stringify(fields ? fields : ["*"]),
        limit_page_length: 0,
    };
    if (filters) {
        params["filters"] = JSON.stringify(filters);
    }

    if (parent) {
        params["parent"] = parent;
    }
    if (sortBy) {
        params["order_by"] = sortBy;
    }

    return axios.get(`resource/` + doctype, { params });
};

export const useGetDocTypeList = ({
    qKey,
    doctype,
    filters,
    fields,
    sortBy,
    limit,
}) => {
    let key = [];
    if (qKey) {
        key = qKey;
    } else {
        key = [`GetDocList_${doctype}${limit ? "_:" + limit + ":" : ""}`];
        if (filters) {
            key.push(...filters);
        }
    }

    const data = useQuery(
        key,
        async () => {
            const response = await fetchList(
                doctype,
                filters,
                fields,
                sortBy,
                null,
                limit
            );
            return response.data.data;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            refetchOnReconnect: true,
            cacheTime: 1000 * 60 * 10,
            enabled: !!doctype,
        }
    );

    return data;
};


export const useGetDocTypeListWithChildren = ({ doctype, filters, fields, order_by }) => {
    const _filters = filters ? (isObject(filters) ? JSON.stringify(filters) : filters) : null;
    let key = [`GetDocList_Expanded_${doctype}`];
    if (_filters) {
      key.push(_filters);
    }
    const data = useQuery(
      key,
      async () => {
        const params = {
          doctype,
          // filters,
          filters: filters ? (isObject(filters) ? JSON.stringify(filters) : filters) : [],
          fields: fields ? (isObject(fields) ? JSON.stringify(fields) : fields) : null,
          order_by,
        };
  
        if (_filters) {
          params.filters = _filters;
        }
        const response = await axios.get("method/custom_api.api.get_list_with_children", { params });
  
        return response.data.message;
      },
      {
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        enabled: !!doctype,
        cacheTime: Infinity,
      }
    );
  
    return data;
  };